import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

const STATUS_COLOR: Record<string, string> = {
  loaded: 'var(--accent-green, #3fb950)',
  skipped: 'var(--text-dimmed)',
  failed: 'var(--accent-red)',
  unloaded: 'var(--accent-blue, #58a6ff)',
};

/**
 * The persisted common-schema cache preview: a per-resourceType summary
 * (mapped / loaded / skipped / failed / not-yet-loaded) plus a paginated browser
 * of the mapped FHIR resources with their load status + Cerbo destination id.
 * This is what the operator inspects to see the common schema the import
 * produced — and resume/diagnose a partial load.
 */
export function FhirCacheView({ sessionId }: { sessionId: string }) {
  const [resourceType, setResourceType] = useState<string | undefined>(undefined);
  const [loadStatus, setLoadStatus] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<string | null>(null);

  const summary = useQuery({
    queryKey: ['fhir-cache-summary', sessionId],
    queryFn: () => api.getFhirCacheSummary(sessionId),
    refetchInterval: 3000,
  });
  const cache = useQuery({
    queryKey: ['fhir-cache', sessionId, resourceType, loadStatus],
    queryFn: () => api.getFhirCache(sessionId, { resourceType, loadStatus, limit: 50 }),
    refetchInterval: 3000,
  });

  const byType = summary.data?.byType ?? [];
  if (summary.isLoading) return <div className="text-[11px]" style={{ color: 'var(--text-dimmed)' }}>Loading cache…</div>;
  if (byType.length === 0) return <div className="text-[11px]" style={{ color: 'var(--text-dimmed)' }}>Nothing in the common-schema cache yet — map + run an import.</div>;

  const cell = 'px-2 py-1 text-right tabular-nums';
  return (
    <div className="space-y-2 text-[12px]">
      {/* per-resource-type summary */}
      <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid var(--border)' }}>
        <table className="text-[11px] w-full">
          <thead>
            <tr style={{ background: 'var(--bg-tertiary)', color: 'var(--text-dimmed)' }}>
              <th className="px-2 py-1 text-left">Resource</th>
              <th className={cell}>Mapped</th>
              <th className={cell}>Loaded</th>
              <th className={cell}>Skipped</th>
              <th className={cell}>Failed</th>
              <th className={cell}>Pending</th>
            </tr>
          </thead>
          <tbody>
            {byType.map((r) => (
              <tr
                key={r.resourceType}
                className="cursor-pointer"
                style={{ borderTop: '1px solid var(--border)', background: resourceType === r.resourceType ? 'var(--bg-tertiary)' : 'transparent' }}
                onClick={() => setResourceType((cur) => (cur === r.resourceType ? undefined : r.resourceType))}
              >
                <td className="px-2 py-1 font-mono" style={{ color: 'var(--text-primary)' }}>{r.resourceType}</td>
                <td className={cell} style={{ color: 'var(--text-primary)' }}>{r.mapped + r.invalid}</td>
                <td className={cell} style={{ color: r.loaded ? STATUS_COLOR.loaded : 'var(--text-dimmed)' }}>{r.loaded}</td>
                <td className={cell} style={{ color: 'var(--text-dimmed)' }}>{r.skipped}</td>
                <td className={cell} style={{ color: r.failed ? STATUS_COLOR.failed : 'var(--text-dimmed)' }}>{r.failed}</td>
                <td className={cell} style={{ color: 'var(--text-dimmed)' }}>{r.notLoaded}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* filters */}
      <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-dimmed)' }}>
        <span>Filter:</span>
        {['loaded', 'skipped', 'failed', 'unloaded'].map((s) => (
          <button
            key={s}
            className="rounded px-1.5 py-0.5"
            style={{
              border: '1px solid var(--border)',
              background: loadStatus === s ? 'var(--bg-tertiary)' : 'transparent',
              color: STATUS_COLOR[s] ?? 'var(--text-dimmed)',
            }}
            onClick={() => setLoadStatus((cur) => (cur === s ? undefined : s))}
          >
            {s}
          </button>
        ))}
        {resourceType ? <span style={{ color: 'var(--text-primary)' }}>· {resourceType} <button onClick={() => setResourceType(undefined)}>✕</button></span> : null}
      </div>

      {/* cache rows */}
      <div className="space-y-1">
        {(cache.data?.rows ?? []).map((row) => {
          const status = row.loadStatus ?? 'unloaded';
          return (
            <div key={row.id} className="rounded-lg" style={{ border: '1px solid var(--border)' }}>
              <button
                className="flex w-full items-center justify-between px-2 py-1.5 text-[11px]"
                onClick={() => setOpen((o) => (o === row.id ? null : row.id))}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{row.resourceType}</span>
                  <span className="font-mono truncate" style={{ color: 'var(--text-dimmed)' }}>{row.fhirId}</span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  {row.destinationId != null ? <span className="font-mono" style={{ color: 'var(--text-dimmed)' }}>#{row.destinationId}</span> : null}
                  <span style={{ color: STATUS_COLOR[status] ?? 'var(--text-dimmed)' }}>● {row.loadAction ?? status}</span>
                </span>
              </button>
              {open === row.id ? (
                <pre className="overflow-x-auto px-2 py-1 text-[10px] font-mono" style={{ borderTop: '1px solid var(--border)', color: 'var(--text-dimmed)' }}>
                  {JSON.stringify(row.error ?? row.resource, null, 2)}
                </pre>
              ) : null}
            </div>
          );
        })}
        {cache.data && cache.data.totalCount > cache.data.rows.length ? (
          <div className="text-[10px]" style={{ color: 'var(--text-dimmed)' }}>Showing {cache.data.rows.length} of {cache.data.totalCount}</div>
        ) : null}
      </div>
    </div>
  );
}
